import { mastiffTick } from './saberes.ts';
import { ARTIFICER, FORKS, NIGHTMARES, SKILL_FX, UNIQUES, type DamageType } from '@somnhold/content';
import { hexToWorld } from '../hex.ts';
import { dist2, iatan2, idiv, imin, withinRange } from '../math/fixed.ts';
import type { BattleState, Bot, Enemy, Structure } from '../types.ts';
import { damageEnemy, enemiesNear, ev, isFlying, targetable } from './combat.ts';
import { stepToward } from './enemies.ts';
import { guardTick } from './bells.ts';
import { cartTick } from './escort.ts';
import { firesAsTower } from './towers.ts';
import { featBurst } from './feats.ts';
import { anvilForge, anvilGuard } from './forms.ts';
import { tormoTick } from './legacy.ts';
import { druidBotHit } from './panoply-druid.ts';
import { hawkDive, hawkReturns } from './panoply-ranger.ts';
import { droneFlight, droneHeals, houndBit, houndScent, lureOf } from './panoply-artificer.ts';

/** A summon's blow, as damage options: a conversion glyph's type (docs/GLIFOS.md §1), else `own` (physical adds nothing). */
const botHit = (b: Bot, own: DamageType = 'physical'): { pid: string; type?: DamageType } => {
  const t = b.dt ?? own;
  return t === 'physical' ? { pid: b.pid } : { pid: b.pid, type: t };
};

/** The artificer's clockwork: automatons sweep and quicken towers, spider-bots run in and blow up. */

export const updateBots = (s: BattleState): void => {
  const bots = s.bots;
  if (!bots || bots.length === 0) return;
  const keep: Bot[] = [];
  for (const b of bots) {
    // Tormo (the artificer's legacy) is never lost: broken, it kneels and gets up (battle/legacy.ts).
    if (b.tormo) {
      tormoTick(s, b);
      keep.push(b);
      continue;
    }
    if (b.hp <= 0 || b.expires <= s.tick) {
      ev(s, { t: 'boom', x: b.x, y: b.y, r: 600, kind: b.hp <= 0 ? 'botBroken' : 'botGone' });
      // The anvil harness: broken, it bursts.
      if (b.hp <= 0 && b.burst) {
        let killed = 0;
        for (const e of enemiesNear(s, b.x, b.y, b.burst.radius, (o) => targetable(s, o))) {
          damageEnemy(s, e, b.burst.damage, botHit(b));
          if (e.hp <= 0) killed++;
        }
        featBurst(s, b.owner, killed);
        ev(s, { t: 'boom', x: b.x, y: b.y, r: b.burst.radius, kind: 'spiderBlast' });
        // The forge (docs/UNICAS.md §3): burning ground, and built again once.
        const again = anvilForge(s, b);
        if (again) keep.push(again);
      }
      continue;
    }
    b.animT++;
    // The saga's caravan only walks its road.
    if (b.kind === 'cart') {
      cartTick(s, b);
      keep.push(b);
      continue;
    }
    // Dorn's guard automatons strike whatever is nearest (Las vías 2).
    if (b.kind === 'guard') {
      guardTick(s, b);
      keep.push(b);
      continue;
    }
    // The anvil harness: it keeps to its maker on a long leash, so the artificer can stand back from the fight.
    if (b.follow) {
      const o = s.heroes.find((h) => h.id === b.owner);
      const f = UNIQUES.anvilHarness.fx;
      const walking = b.anim === 'walk';
      if (o && o.downTicks === 0 && !withinRange(b.x, b.y, o.x, o.y, walking ? (f.near as number) : (f.leash as number))) {
        b.anim = 'walk';
        stepToward(b, o.x, o.y, 110);
      } else if (walking) b.anim = 'idle';
      // The iron guard: its maker takes less beside it.
      if (o?.uqForm && o.downTicks === 0) anvilGuard(s, b, o);
    }
    if (b.kind === 'spider') {
      if (!spiderTick(s, b)) keep.push(b);
      continue;
    }
    if (b.kind === 'hawk') {
      hawkTick(s, b);
      keep.push(b);
      continue;
    }
    // The kennel's mastiffs (docs/SABERES.md §4.1): they hunt the ones going round, bite and hold.
    if (b.kind === 'mastiff') {
      mastiffTick(s, b);
      keep.push(b);
      continue;
    }
    if (b.kind === 'drone') {
      droneTick(s, b);
      keep.push(b);
      continue;
    }
    // The clockwork hound and the living trees walk to the nearest nightmare, then strike as the automaton does.
    // So do the unique forms' own: Gaspar's guard and the old bear of bark.
    if (b.kind === 'hound' || b.kind === 'treant' || b.kind === 'gasparGuard' || b.kind === 'barkBear') walkerTick(s, b);
    // Towers around it work faster while it stands.
    for (const st of s.structures) {
      if (st.destroyed || !firesAsTower(st)) continue;
      const c = hexToWorld(st.q, st.r);
      if (!withinRange(b.x, b.y, c.x, c.y, b.aura)) continue;
      if (st.buffTicks < 2) st.buffTicks = 2;
      if (st.buffBp < b.auraBp) st.buffBp = b.auraBp;
    }
    if (b.cd > 0) b.cd--;
    else {
      const hits = enemiesNear(s, b.x, b.y, b.radius, (e) => targetable(s, e));
      if (hits.length > 0) {
        const first = hits[0] as Enemy;
        b.facing = iatan2(first.y - b.y, first.x - b.x);
        for (const e of hits) {
          const dealt = damageEnemy(s, e, b.damage, botHit(b));
          // The druid's panoply: the shadow wolves' blows, the trees that hold.
          if (b.uq) druidBotHit(s, b, e, dealt);
        }
        // The kennel coat (docs/PANOPLIA.md §10): what the hound bites is held.
        if (b.uq) houndBit(s, b, hits);
        b.cd = b.every;
        b.anim = 'attack';
        b.animT = 0;
        ev(s, { t: 'boom', x: b.x, y: b.y, r: b.radius, kind: 'sweep' });
      } else if (b.anim === 'attack' && b.animT > 6) b.anim = 'idle';
    }
    keep.push(b);
  }
  s.bots = keep;
};

/** A hound or a tree: it goes for the nearest nightmare within its hunt of its maker (or stays by its maker). */
const walkerTick = (s: BattleState, b: Bot): void => {
  const fx = b.kind === 'hound' ? SKILL_FX.clockHound : SKILL_FX.forestAwakening;
  const o = s.heroes.find((h) => h.id === b.owner);
  const ax = o && o.downTicks === 0 ? o.x : b.x;
  const ay = o && o.downTicks === 0 ? o.y : b.y;
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of enemiesNear(s, ax, ay, fx.hunt, (x) => targetable(s, x) && !isFlying(x))) {
    const d = dist2(e.x, e.y, b.x, b.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  // Olfato (the kennel coat's final form): the nearest elite first.
  if (b.uq) best = houndScent(s, b, best, ax, ay, fx.hunt);
  if (best && !withinRange(b.x, b.y, best.x, best.y, idiv(b.radius * 3, 4))) {
    b.anim = 'walk';
    b.facing = iatan2(best.y - b.y, best.x - b.x);
    stepToward(b, best.x, best.y, fx.step);
  } else if (b.anim === 'walk') b.anim = 'idle';
};

/** A spider-bot runs at the nearest nightmare it can see and blows up next to it; returns true when spent. */
const spiderTick = (s: BattleState, b: Bot): boolean => {
  const w = FORKS.swarm;
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of enemiesNear(s, b.x, b.y, w.hunt, (o) => targetable(s, o) && !isFlying(o))) {
    const d = dist2(e.x, e.y, b.x, b.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  if (!best) {
    b.anim = 'idle';
    return false;
  }
  if (!withinRange(b.x, b.y, best.x, best.y, w.reach)) {
    b.anim = 'walk';
    stepToward(b, best.x, best.y, w.step);
    return false;
  }
  for (const e of enemiesNear(s, b.x, b.y, b.radius, (o) => targetable(s, o))) damageEnemy(s, e, b.damage, botHit(b));
  ev(s, { t: 'boom', x: b.x, y: b.y, r: b.radius, kind: 'spiderBlast' });
  return true;
};

/**
 * The ranger's hawk: it hunts within its reach of the ranger (the same prey while it lives there), flies to it
 * and strikes every pulse, marking it (and leaving it bleeding, with talons); with nothing to hunt it keeps
 * close to the ranger. Nightmares can't reach it.
 */
const hawkTick = (s: BattleState, b: Bot): void => {
  const o = s.heroes.find((h) => h.id === b.owner);
  if (b.cd > 0) b.cd--;
  if (!o || o.downTicks > 0) {
    b.anim = 'idle';
    return;
  }
  const fx = SKILL_FX.hawk;
  const inReach = (e: Enemy): boolean => targetable(s, e) && withinRange(o.x, o.y, e.x, e.y, b.radius);
  let prey = b.prey ? s.enemies.find((e) => e.id === b.prey && e.hp > 0) : undefined;
  if (!prey || !inReach(prey)) {
    // The falconer's jerkin's final form: its prey dead, the hawk takes the biggest at once.
    prey = b.prey && !prey && o.uq?.length ? hawkReturns(s, o, inReach) : undefined;
    let bd = 0x7fffffff;
    if (!prey)
      for (const e of s.enemies) {
        if (e.hp <= 0 || !inReach(e)) continue;
        const d = dist2(e.x, e.y, o.x, o.y);
        if (d < bd) {
          bd = d;
          prey = e;
        }
      }
  }
  b.prey = prey?.id;
  if (!prey) {
    b.anim = 'walk';
    if (!withinRange(b.x, b.y, o.x, o.y, 600)) stepToward(b, o.x, o.y, fx.step);
    return;
  }
  if (!withinRange(b.x, b.y, prey.x, prey.y, fx.reach)) {
    b.anim = 'walk';
    stepToward(b, prey.x, prey.y, fx.step);
    return;
  }
  b.facing = iatan2(prey.y - b.y, prey.x - b.x);
  // The falconer's jerkin: every few seconds, a dive that stuns.
  if (o.uq?.length) hawkDive(s, b, o, prey);
  if (b.cd > 0) return;
  damageEnemy(s, prey, b.damage, botHit(b));
  if (b.markBp && (!prey.markTicks || prey.markTicks <= 0 || (prey.markBp ?? 0) <= b.markBp)) {
    prey.markBp = b.markBp;
    prey.markTicks = fx.markTicks;
  }
  if (b.bleed) {
    prey.dotDmg = b.bleed.damage;
    prey.dotType = b.dt ?? 'physical';
    prey.dotEvery = b.bleed.every;
    prey.dotTicks = b.bleed.ticks;
    prey.lastPid = b.pid;
  }
  b.cd = b.every;
  b.anim = 'attack';
  b.animT = 0;
  ev(s, { t: 'boom', x: prey.x, y: prey.y, r: 300, kind: 'hawkStrike' });
};

/**
 * The artificer's repair drone: it flies to the most hurt standing structure within its reach of the
 * artificer (the same one until it's whole) and mends it every pulse; the welder's sparks the nearest
 * nightmare as it works. With nothing to mend it hovers by the artificer. Nightmares can't reach it.
 */
const droneTick = (s: BattleState, b: Bot): void => {
  const o = s.heroes.find((h) => h.id === b.owner);
  if (b.cd > 0) b.cd--;
  if (!o) return;
  const fx = SKILL_FX.repairDrone;
  const hurt = (st: Structure): boolean => !st.destroyed && st.maxHp > 0 && st.hp < st.maxHp;
  let target = b.mend ? s.structures.find((st) => st.id === b.mend) : undefined;
  if (!target || !hurt(target)) {
    target = undefined;
    let worst = 10001;
    for (const st of s.structures) {
      if (!hurt(st)) continue;
      const c = hexToWorld(st.q, st.r);
      if (!withinRange(o.x, o.y, c.x, c.y, b.radius)) continue;
      const left = idiv(st.hp * 10000, st.maxHp);
      if (left < worst) {
        worst = left;
        target = st;
      }
    }
  }
  b.mend = target?.id;
  // The clockmaker's gear (docs/PANOPLIA.md §10): with nothing to mend, the hurt allies.
  if (!target && b.uq && droneHeals(s, b, o)) return;
  if (!target) {
    if (!withinRange(b.x, b.y, o.x, o.y, 700)) stepToward(b, o.x, o.y, fx.step);
    b.anim = 'walk';
    return;
  }
  const c = hexToWorld(target.q, target.r);
  if (!withinRange(b.x, b.y, c.x, c.y, fx.reach)) {
    b.anim = 'walk';
    stepToward(b, c.x, c.y, fx.step);
    return;
  }
  if (b.cd > 0) return;
  const was = target.hp;
  target.hp = imin(target.maxHp, target.hp + b.damage);
  if (b.uq) droneFlight(s, b, o, target.hp - was);
  b.cd = b.every;
  b.anim = 'attack';
  b.animT = 0;
  ev(s, { t: 'boom', x: c.x, y: c.y, r: 400, kind: 'droneMend' });
  if (b.spark) {
    const e = enemiesNear(s, b.x, b.y, b.spark.radius, (x) => targetable(s, x))[0];
    if (e) {
      damageEnemy(s, e, b.spark.damage, botHit(b, 'lightning'));
      ev(s, { t: 'boom', x: e.x, y: e.y, r: 300, kind: 'droneSpark' });
    }
  }
};

/** A structure covered by a repair drone with armor plates: what it takes (bp; 10000 = none). */
export const droneCoverBp = (s: BattleState, st: Structure): number => {
  const c = hexToWorld(st.q, st.r);
  let bp = 10000;
  for (const b of s.bots ?? []) if (b.cover && b.cover.takenBp < bp && withinRange(b.x, b.y, c.x, c.y, b.cover.radius)) bp = b.cover.takenBp;
  return bp;
};

/** The automaton nearest to a nightmare that draws it (within its pull), if any; a siege engine heeds only the caravan. */
export const drawnBy = (s: BattleState, e: Enemy): Bot | null => {
  const siege = NIGHTMARES[e.kind].siege;
  // Drawn by a hound's scent (docs/PANOPLIA.md §10).
  if (e.lureUntil && !siege) {
    const lure = lureOf(s, e);
    if (lure) return lure;
  }
  let best: Bot | null = null;
  let bd = 0x7fffffff;
  for (const b of s.bots ?? []) {
    if (b.taunt <= 0 || b.hp <= 0 || (siege && b.kind !== 'cart')) continue;
    const d = dist2(e.x, e.y, b.x, b.y);
    if (d > b.taunt * b.taunt || d >= bd) continue;
    bd = d;
    best = b;
  }
  return best;
};

/**
 * The hero's automaton nearest to a nightmare that hunts heroes (crypts, bosses) and is drawn by it, if any: no
 * structure hunter or flyer heeds it (as on the bastion), and a boss only for its first moments (`bossUntil`).
 */
export const huntDrawnBy = (s: BattleState, e: Enemy, boss: boolean): Bot | null => {
  if (NIGHTMARES[e.kind].structureHunter || NIGHTMARES[e.kind].siege || isFlying(e)) return null;
  // Drawn by a hound's scent (docs/PANOPLIA.md §10), in a crypt too.
  if (e.lureUntil) {
    const lure = lureOf(s, e);
    if (lure) return lure;
  }
  let best: Bot | null = null;
  let bd = 0x7fffffff;
  for (const b of s.bots ?? []) {
    if ((b.kind !== 'automaton' && b.kind !== 'colossus') || b.taunt <= 0 || b.hp <= 0) continue;
    if (boss && !((b.bossUntil ?? 0) > s.tick)) continue;
    const d = dist2(e.x, e.y, b.x, b.y);
    if (d > b.taunt * b.taunt || d >= bd) continue;
    bd = d;
    best = b;
  }
  return best;
};

/** Upkeep (artificer passive): structures near a standing artificer mend a little every second of a fight. */
export const artificerUpkeep = (s: BattleState): void => {
  const u = ARTIFICER.upkeep;
  if (s.tick % u.every !== 0) return;
  for (const h of s.heroes) {
    if (h.cls !== 'artificer' || h.downTicks > 0) continue;
    for (const st of s.structures) {
      if (st.destroyed || st.maxHp <= 0 || st.hp >= st.maxHp) continue;
      const c = hexToWorld(st.q, st.r);
      if (!withinRange(h.x, h.y, c.x, c.y, u.radius)) continue;
      st.hp += u.hp;
      if (st.hp > st.maxHp) st.hp = st.maxHp;
    }
  }
};
